import { useMemo, useState } from 'react';
import { exerciseNavTarget } from '@/lib/exercise-route';
import { useParams, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, cn, MathText } from '@jeraisonne/ui';
import { ArrowLeft, Clock, Minus, Plus, Trash2 } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { useLaunchPending } from '@/hooks/use-launch-pending';
import { supabase } from '@/lib/supabase';
import {
  getActiveObjectives,
  updateObjective,
  deactivateObjective,
} from '@/lib/student-objectives/student-objectives';
import {
  getObjectiveProgress,
  isCardsObjective,
} from '@/lib/student-objectives/objective-progress';
import { progressFromCards, useObjectiveScope } from '@/lib/student-objectives/objective-scope';
import { getExerciseIdsForObjective } from '@/lib/student-objectives/objective-filters';
import { getTutorAssignments } from '@/lib/student-objectives/tutor-assignments';
import { getClassAssignments } from '@/lib/student-objectives/class-assignments';
import {
  getEpreuvesForObjective,
  computeEpreuveProgressMap,
  sortEpreuvesByRecency,
} from '@/lib/student-objectives/epreuve-lib';
import type { EpreuveProgress, EpreuveExerciseAttempt } from '@/lib/student-objectives/epreuve-lib';
import type { AppEpreuve, ObjectiveProgress } from '@/lib/student-objectives/objective-types';
import {
  EPREUVE_TARGET_MAX,
  EPREUVE_TARGET_MIN,
  getEpreuveTargetCount,
} from '@/lib/student-objectives/objective-types';
import type { ExerciseWithAccess } from '@/components/student-objectives/objective-detail-view';
import { ObjectiveDetailView } from '@/components/student-objectives/objective-detail-view';
import { ObjectivePaywallBlock } from '@/components/student-objectives/objective-paywall-block';
import { DeadlineEditor } from '@/components/student-objectives/deadline-editor';
import {
  computeDeadlineCountdown,
  getDeadlineBadgeClasses,
} from '@/lib/student-objectives/deadline-format';
import { useInProgressAttemptIds } from '@/lib/exercises/use-in-progress-attempts';
import type { AppStudentObjective } from '@/lib/student-objectives/objective-types';

const EXAM_TYPE_LABEL: Record<string, string> = {
  brevet: 'Brevet',
  bac: 'Bac',
  bac_pro: 'Bac Pro',
  controle_commun: 'Contrôle commun',
  ds: 'DS',
  autre: 'Autre',
};

function EpreuveTargetStepper({ objective }: { objective: AppStudentObjective }) {
  const queryClient = useQueryClient();
  const initial = getEpreuveTargetCount(objective.customFilters);
  const [value, setValue] = useState(initial);
  const [saving, setSaving] = useState(false);

  async function commit(next: number) {
    if (next === value) return;
    setValue(next);
    setSaving(true);
    try {
      await updateObjective(supabase, objective.id, {
        customFilters: { ...objective.customFilters, target_epreuve_count: next },
      });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['student-objectives'] }),
        queryClient.invalidateQueries({ queryKey: ['objective-progress', objective.id] }),
        queryClient.invalidateQueries({ queryKey: ['objectives-progress'] }),
      ]);
    } finally {
      setSaving(false);
    }
  }

  const dec = Math.max(EPREUVE_TARGET_MIN, value - 1);
  const inc = Math.min(EPREUVE_TARGET_MAX, value + 1);
  const atMin = value <= EPREUVE_TARGET_MIN;
  const atMax = value >= EPREUVE_TARGET_MAX;

  return (
    <div className="border-ink-100 flex items-center justify-between gap-3 rounded-2xl border bg-white/70 p-3 backdrop-blur-sm">
      <div className="min-w-0">
        <p className="font-display text-ink-900 text-[14px] font-semibold">
          Combien d’épreuves veux-tu faire&nbsp;?
        </p>
        <p className="text-ink-500 mt-0.5 font-sans text-[12px]">Entre 1 et 10 épreuves.</p>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => commit(dec)}
          disabled={atMin || saving}
          aria-label="Diminuer"
          className="border-ink-200 text-ink-700 hover:bg-ink-50 flex h-11 w-11 items-center justify-center rounded-full border bg-white transition-all active:scale-95 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <Minus className="h-4 w-4" />
        </button>
        <span
          aria-live="polite"
          className="text-ink-900 font-display w-8 text-center text-lg font-bold tabular-nums"
        >
          {value}
        </span>
        <button
          type="button"
          onClick={() => commit(inc)}
          disabled={atMax || saving}
          aria-label="Augmenter"
          className="border-ink-200 text-ink-700 hover:bg-ink-50 flex h-11 w-11 items-center justify-center rounded-full border bg-white transition-all active:scale-95 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function EpreuveObjectiveView({
  objective,
  epreuves,
  epreuveProgress,
  progress,
  isLoading,
  onBack,
  onStart,
}: {
  objective: AppStudentObjective;
  epreuves: AppEpreuve[];
  epreuveProgress: Record<string, EpreuveProgress>;
  progress: ObjectiveProgress | null;
  isLoading: boolean;
  onBack: () => void;
  onStart: (epreuveId: string) => void;
}) {
  const illustrationUrl = objective.template.illustrationUrl ?? null;
  const hasIllustration = !!illustrationUrl;
  const countdown = computeDeadlineCountdown(objective.deadline);
  const targetCount =
    progress?.targetEpreuveCount ?? getEpreuveTargetCount(objective.customFilters);
  const engaged = progress?.engagedEpreuveCount ?? 0;
  const mastery = progress?.masteryPercent ?? 0;
  const completedExos = progress?.completedExercises ?? 0;
  const totalExos = progress?.totalExercises ?? 0;
  // Plus récemment révisées d'abord, puis les non-commencées.
  const sortedEpreuves = sortEpreuvesByRecency(epreuves, epreuveProgress);

  return (
    <div className="space-y-4">
      <div
        className={cn(
          'relative overflow-hidden rounded-2xl',
          hasIllustration && 'sm:min-h-88 min-h-72',
        )}
      >
        {hasIllustration && (
          <>
            <img
              src={illustrationUrl!}
              alt=""
              loading="lazy"
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 h-full w-full object-cover"
            />
            <div
              aria-hidden="true"
              className="bg-linear-to-b via-ink-50/60 to-ink-50/95 pointer-events-none absolute inset-0 from-transparent"
            />
          </>
        )}
        <button
          type="button"
          onClick={onBack}
          aria-label="Retour au dashboard"
          className={cn(
            'absolute left-3 top-3 z-10 inline-flex h-11 w-11 items-center justify-center rounded-full border shadow-sm transition-colors active:scale-95',
            hasIllustration
              ? 'border-ink-100 text-ink-700 bg-white/85 backdrop-blur hover:bg-white'
              : 'border-ink-200 text-ink-700 hover:bg-ink-50 bg-white',
          )}
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        {countdown && (
          <span
            className={cn(
              'absolute right-3 top-3 z-10 inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 font-sans text-[12px] font-semibold tabular-nums',
              getDeadlineBadgeClasses(countdown.urgency),
            )}
            aria-label={`Date butoir : ${countdown.formatted}`}
          >
            <Clock className="h-3.5 w-3.5" aria-hidden="true" />
            {countdown.formatted}
          </span>
        )}
        <div className={cn(hasIllustration ? 'absolute inset-x-0 bottom-0 p-4' : 'relative pt-16')}>
          <h1 className="font-display text-ink-900 text-2xl font-bold">
            {objective.template.title}
          </h1>
          {objective.template.description && (
            <p className="text-ink-500 mt-1 font-sans text-[14px]">
              {objective.template.description}
            </p>
          )}
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 font-sans text-[13px]">
            <span className="text-primary-700 font-semibold">
              {engaged}/{targetCount} épreuves
            </span>
            {totalExos > 0 && (
              <>
                <span className="text-ink-500">
                  {completedExos}/{totalExos} exercices
                </span>
                <span className="text-accent-600 font-semibold">{mastery}% maîtrise</span>
              </>
            )}
          </div>
        </div>
      </div>

      <DeadlineEditor objective={objective} />

      <EpreuveTargetStepper objective={objective} />

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2].map((i) => (
            <div key={i} className="bg-ink-100 h-16 animate-pulse rounded-xl" />
          ))}
        </div>
      ) : epreuves.length === 0 ? (
        <div className="border-ink-100 rounded-2xl border py-12 text-center">
          <p className="text-ink-500 font-sans text-[14px]">
            Aucune épreuve disponible pour votre niveau.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {sortedEpreuves.map((e) => {
            const ep = epreuveProgress[e.id];
            const total = ep?.total ?? 0;
            const done = ep?.done ?? 0;
            const allDone = total > 0 && done === total;
            return (
              <div
                key={e.id}
                className="border-ink-100 flex items-center gap-4 rounded-2xl border bg-white px-4 py-3"
              >
                <div className="min-w-0 flex-1">
                  <p className="font-display text-ink-900 text-[15px] font-semibold">
                    <MathText>{e.title}</MathText>
                  </p>
                  <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                    <span className="text-ink-500 font-sans text-[12px]">
                      {e.examType
                        ? `${EXAM_TYPE_LABEL[e.examType] ?? e.examType}${e.examYear ? ` ${e.examYear}` : ''}`
                        : ''}
                    </span>
                    {total > 0 && (
                      <span
                        className={cn(
                          'rounded-full px-1.5 font-sans text-[11px] font-semibold tabular-nums',
                          allDone
                            ? 'bg-xp-50 text-xp-700'
                            : done > 0
                              ? 'bg-primary-50 text-primary-700'
                              : 'bg-ink-100 text-ink-500',
                        )}
                      >
                        {done}/{total} exercices
                      </span>
                    )}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant={done > 0 ? 'outline' : 'primary'}
                  onClick={() => onStart(e.id)}
                >
                  {allDone ? 'Revoir' : done > 0 ? 'Continuer' : 'Ouvrir'}
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function DeleteObjectiveButton({ objectiveId, title }: { objectiveId: string; title: string }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);

  async function handleDelete() {
    setDeleting(true);
    try {
      await deactivateObjective(supabase, objectiveId);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['student-objectives'] }),
        queryClient.invalidateQueries({ queryKey: ['objectives-progress'] }),
      ]);
      navigate({ to: '/student/dashboard' });
    } finally {
      setDeleting(false);
    }
  }

  if (!confirming) {
    return (
      <button
        type="button"
        onClick={() => setConfirming(true)}
        className="text-ink-400 mx-auto flex min-h-11 w-full items-center justify-center gap-2 rounded-2xl px-4 font-sans text-sm font-semibold transition-colors hover:bg-red-50 hover:text-red-600 active:scale-[0.98] sm:w-auto"
      >
        <Trash2 className="h-4 w-4" />
        Retirer cet objectif de ma liste
      </button>
    );
  }

  return (
    <div className="space-y-3 rounded-2xl border border-red-100 bg-red-50/60 p-4">
      <p className="text-ink-700 font-sans text-sm">
        Retirer «&nbsp;{title}&nbsp;» de ta liste d’objectifs&nbsp;? Tu pourras le rajouter plus
        tard.
      </p>
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setConfirming(false)}
          disabled={deleting}
          className="w-full sm:w-auto"
        >
          Annuler
        </Button>
        <Button
          variant="destructive"
          size="sm"
          onClick={handleDelete}
          disabled={deleting}
          className="w-full sm:w-auto"
        >
          {deleting ? 'Suppression…' : 'Retirer'}
        </Button>
      </div>
    </div>
  );
}

export function ObjectiveDetailPage() {
  const { objectiveId } = useParams({ strict: false }) as { objectiveId: string };
  const { user } = useAuth();
  const navigate = useNavigate();
  const studentId = user?.id ?? '';

  // Load student's active objectives and find the one matching the param
  const { data: objectives = [], isLoading: loadingObjectives } = useQuery({
    queryKey: ['student-objectives', studentId],
    enabled: !!studentId,
    queryFn: () => getActiveObjectives(supabase, studentId),
  });

  const objective = objectives.find((o) => o.id === objectiveId) ?? null;

  // Student level (for the chapter filter)
  const { data: studentLevel = null } = useQuery({
    queryKey: ['student-level', studentId],
    enabled: !!studentId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('student_profiles')
        .select('level')
        .eq('id', studentId)
        .maybeSingle();
      if (error) throw error;
      return data?.level ?? null;
    },
    staleTime: 5 * 60 * 1000,
  });

  // Objectifs SKILLCARDS (migration 0514) : périmètre complet en une requête.
  const cardsObjective = !!objective && isCardsObjective(objective);
  const { data: scope = null, isLoading: loadingScope } = useObjectiveScope(
    objectiveId,
    cardsObjective,
  );

  // Progress (épreuves et ancien diagnostic : calcul historique)
  const { data: legacyProgress = null } = useQuery({
    queryKey: ['objective-progress', objectiveId, studentId, studentLevel],
    enabled: !!objective && !cardsObjective,
    queryFn: () => getObjectiveProgress(supabase, studentId, objective!, studentLevel),
  });
  const progress = useMemo(
    () =>
      cardsObjective && scope
        ? progressFromCards(
            objectiveId,
            objective!.template.type,
            scope.progress,
            scope.packs.map((p) => p.title),
          )
        : legacyProgress,
    [cardsObjective, scope, legacyProgress, objectiveId, objective],
  );

  // Assignments (tutors + classes) — used to tag and sort exercises. Un
  // élève peut avoir plusieurs tuteurs actifs (migration 0227).
  const { data: tutorAssignments = [] } = useQuery({
    queryKey: ['tutor-assignments', studentId],
    enabled: !!studentId,
    queryFn: () => getTutorAssignments(supabase, studentId),
    staleTime: 30_000,
  });

  const { data: classAssignments = [] } = useQuery({
    queryKey: ['class-assignments', studentId],
    enabled: !!studentId,
    queryFn: () => getClassAssignments(supabase, studentId),
    staleTime: 30_000,
  });

  type RawExercise = {
    id: string;
    title: string;
    difficulty: ExerciseWithAccess['difficulty'];
    estimatedMinutes?: number | null;
    subtitle: string;
    illustrationUrl: string | null;
  };

  // Ancien objectif Diagnostic : exercices `is_diagnostic` du niveau.
  const { data: diagnosticExercises = [], isLoading: loadingDiagnostic } = useQuery({
    queryKey: ['objective-exercises', objectiveId, studentLevel],
    enabled: !!objective && !cardsObjective && objective.template.contentType !== 'epreuves',
    queryFn: async (): Promise<RawExercise[]> => {
      const ids = await getExerciseIdsForObjective(supabase, objective!, studentLevel);
      if (ids.length === 0) return [];

      const { data: exRows } = await supabase
        .from('exercises')
        .select(
          'id, title, difficulty, estimated_minutes, illustration_url, chapters ( title, illustration_url )',
        )
        .in('id', ids)
        .eq('status', 'published')
        .order('difficulty');

      if (!exRows) return [];

      const rows = exRows as unknown as Array<{
        id: string;
        title: string;
        difficulty: ExerciseWithAccess['difficulty'];
        estimated_minutes: number;
        illustration_url: string | null;
        chapters: { title: string; illustration_url: string | null } | null;
      }>;

      return rows.map((ex) => ({
        id: ex.id,
        title: ex.title,
        difficulty: ex.difficulty,
        estimatedMinutes: ex.estimated_minutes,
        subtitle: ex.chapters?.title ?? '',
        illustrationUrl: ex.illustration_url ?? ex.chapters?.illustration_url ?? null,
      }));
    },
  });

  // Objectifs SKILLCARDS : exercices reliés aux cartes, déjà ordonnés par le serveur.
  const rawExercises = useMemo<RawExercise[]>(() => {
    if (!cardsObjective) return diagnosticExercises;
    const cardTitle = new Map((scope?.cards ?? []).map((c) => [c.id, c.title]));
    return (scope?.exercises ?? []).map((ex) => ({
      id: ex.id,
      title: ex.title,
      difficulty: ex.difficulty,
      subtitle: cardTitle.get(ex.card_id) ?? '',
      illustrationUrl: ex.illustration_url,
    }));
  }, [cardsObjective, diagnosticExercises, scope]);
  const loadingExercises = cardsObjective ? loadingScope : loadingDiagnostic;

  const { data: inProgressIds = [] } = useInProgressAttemptIds(studentId);

  // Enrich with assignment info and sort: assigned first, then by difficulty
  const DIFF_ORDER = { easy: 0, medium: 1, hard: 2 } as const;
  const exercises = useMemo<ExerciseWithAccess[]>(() => {
    const assignmentMap = new Map<string, ExerciseWithAccess['assignment']>();
    for (const tutorAssignment of tutorAssignments) {
      for (const a of tutorAssignment.assignments) {
        assignmentMap.set(a.exerciseId, {
          source: 'tutor',
          label: `Prof : ${tutorAssignment.tutorName}`,
        });
      }
    }
    for (const cls of classAssignments) {
      for (const a of cls.assignments) {
        if (!assignmentMap.has(a.exerciseId)) {
          assignmentMap.set(a.exerciseId, {
            source: 'class',
            label: `Classe : ${cls.className}`,
          });
        }
      }
    }

    return [...rawExercises]
      .map((ex) => ({
        ...ex,
        assignment: assignmentMap.get(ex.id),
        inProgress: inProgressIds.includes(ex.id),
      }))
      .sort((a, b) => {
        const aAssigned = a.assignment ? 0 : 1;
        const bAssigned = b.assignment ? 0 : 1;
        if (aAssigned !== bAssigned) return aAssigned - bAssigned;
        // Objectifs SKILLCARDS : ordre du serveur (essais gratuits, ordre des packs).
        if (cardsObjective) return 0;
        return (DIFF_ORDER[a.difficulty] ?? 0) - (DIFF_ORDER[b.difficulty] ?? 0);
      });
  }, [rawExercises, tutorAssignments, classAssignments, inProgressIds, cardsObjective]);

  // ── Branche épreuves ──────────────────────────────────────────────────────
  const isEpreuveObjective = objective?.template.contentType === 'epreuves';

  const { data: epreuves = [], isLoading: loadingEpreuves } = useQuery({
    queryKey: ['objective-epreuves', objectiveId],
    enabled: !!objective && isEpreuveObjective,
    queryFn: () => getEpreuvesForObjective(supabase, objective!.template),
  });

  // Progression par épreuve (exos faits / total) + dernière révision → tri.
  const epreuveIds = epreuves.map((e) => e.id);
  const { data: epreuveProgress = {} as Record<string, EpreuveProgress> } = useQuery({
    queryKey: ['objective-epreuve-progress', studentId, epreuveIds],
    enabled: !!studentId && epreuveIds.length > 0,
    staleTime: 30_000,
    queryFn: async () => {
      const { data: links } = await supabase
        .from('epreuve_exercises')
        .select('epreuve_id, exercise_id')
        .in('epreuve_id', epreuveIds);
      const exIds = (links ?? []).map((l) => l.exercise_id);
      let completed: EpreuveExerciseAttempt[] = [];
      if (exIds.length > 0) {
        const { data: att } = await supabase
          .from('attempts')
          .select('exercise_id, completed_at')
          .eq('student_id', studentId)
          .eq('status', 'completed')
          .in('exercise_id', exIds);
        completed = (att ?? [])
          .filter((r): r is typeof r & { exercise_id: string } => r.exercise_id != null)
          .map((r) => ({ exercise_id: r.exercise_id, completed_at: r.completed_at }));
      }
      return computeEpreuveProgressMap(links ?? [], completed);
    },
  });

  const { pendingId: startingExerciseId, launch } = useLaunchPending();

  // Spinner sur le bouton cliqué jusqu'à l'ouverture de l'exercice.
  function handleStartExercise(exerciseId: string) {
    void launch(exerciseId, () => openExercise(exerciseId)).catch(console.error);
  }

  async function openExercise(exerciseId: string): Promise<boolean> {
    const { data: ex } = await supabase
      .from('exercises')
      .select('chapter_id, chapters ( id, title, subject, level, slug )')
      .eq('id', exerciseId)
      .single();
    if (!ex) return false;
    const ch = ex.chapters as unknown as {
      id: string;
      title: string;
      subject: string;
      level: string;
      slug: string;
    } | null;
    // Exercice sans chapitre (SKILLCARD) : lecteur direct.
    navigate(exerciseNavTarget(exerciseId, ch));
    return true;
  }

  if (loadingObjectives) {
    return (
      <div className="mx-auto max-w-2xl animate-pulse space-y-4 px-4 py-6 sm:py-8">
        <div className="bg-ink-100 h-6 w-48 rounded" />
        <div className="bg-ink-100 h-4 w-32 rounded" />
      </div>
    );
  }

  if (!objective) {
    return (
      <div className="mx-auto max-w-2xl space-y-4 px-4 py-6 text-center sm:py-8">
        <p className="text-ink-500">Objectif introuvable.</p>
        <Button variant="outline" onClick={() => navigate({ to: '/student/dashboard' })}>
          ← Retour au dashboard
        </Button>
      </div>
    );
  }

  const handleBack = () => navigate({ to: '/student/dashboard' });

  return (
    <div className="pb-safe mx-auto max-w-2xl space-y-6 px-4 py-6 sm:py-8">
      {isEpreuveObjective ? (
        <EpreuveObjectiveView
          objective={objective}
          epreuves={epreuves}
          epreuveProgress={epreuveProgress}
          progress={progress}
          isLoading={loadingEpreuves}
          onBack={handleBack}
          onStart={(epreuveId: string) =>
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            navigate({ to: '/learn/epreuve/$epreuveId' as any, params: { epreuveId } as any })
          }
        />
      ) : (
        <ObjectiveDetailView
          objective={objective}
          scope={scope}
          studentLevel={studentLevel}
          progress={progress}
          exercises={exercises}
          isLoadingExercises={loadingExercises}
          onBack={handleBack}
          onStartExercise={handleStartExercise}
          startingExerciseId={startingExerciseId}
        />
      )}

      <ObjectivePaywallBlock objective={objective} />

      <DeleteObjectiveButton objectiveId={objective.id} title={objective.template.title} />
    </div>
  );
}
